"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * DegreePlansListPage - Cohort List page
 *
 * Two-view toggle matching Angular's ListViewComponent:
 * - When no cohort selected: Org select + CohortPlansDataTable
 * - When cohort selected: CohortPlanDetailView (drill-down)
 */
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const DegreePlansProvider_1 = require("../../providers/DegreePlansProvider");
const ToastProvider_1 = require("../../providers/ToastProvider");
const useOrgLookupData_1 = require("../../hooks/useOrgLookupData");
const useResolvedDegreePlans_1 = require("../../hooks/useResolvedDegreePlans");
const OrganizationSelect_1 = require("../../components/OrganizationSelect");
const CohortPlansDataTable_1 = require("../../components/CohortPlansDataTable");
const CohortPlanDetailView_1 = require("../../components/CohortPlanDetailView");
const LoadingSpinner_1 = require("../../components/LoadingSpinner");
const DegreePlansListPage = () => {
    var _a, _b;
    const { fetchCohortPlansByOrg, getCohortMasterPlans, loading, error } = (0, DegreePlansProvider_1.useDegreePlans)();
    const { error: showError } = (0, ToastProvider_1.useToast)();
    const navigate = (0, react_router_dom_1.useNavigate)();
    const location = (0, react_router_dom_1.useLocation)();
    const locationState = location.state;
    const [selectedOrgId, setSelectedOrgId] = (0, react_1.useState)((_a = locationState === null || locationState === void 0 ? void 0 : locationState.orgId) !== null && _a !== void 0 ? _a : '');
    const [selectedOrg, setSelectedOrg] = (0, react_1.useState)((_b = locationState === null || locationState === void 0 ? void 0 : locationState.org) !== null && _b !== void 0 ? _b : null);
    const [selectedCohortPlan, setSelectedCohortPlan] = (0, react_1.useState)(null);
    // Load cohort plans when organization changes
    (0, react_1.useEffect)(() => {
        if (selectedOrgId) {
            fetchCohortPlansByOrg(selectedOrgId).catch((err) => {
                showError(err instanceof Error ? err.message : 'Failed to load cohort plans');
            });
        }
    }, [selectedOrgId, fetchCohortPlansByOrg, showError]);
    const handleOrganizationChange = (0, react_1.useCallback)((orgId, org) => {
        setSelectedOrgId(orgId);
        setSelectedOrg(org);
        setSelectedCohortPlan(null);
    }, []);
    const handleViewPlans = (0, react_1.useCallback)((plan) => {
        setSelectedCohortPlan(plan);
    }, []);
    const handleMassUpdate = (0, react_1.useCallback)((plan) => {
        var _a, _b;
        if (((_a = plan.cohort) === null || _a === void 0 ? void 0 : _a.id) && ((_b = plan.organization) === null || _b === void 0 ? void 0 : _b.id)) {
            navigate(`/degree-planning/cohort/${plan.cohort.id}/organization/${plan.organization.id}/edit`);
        }
    }, [navigate]);
    const handleBackToList = (0, react_1.useCallback)(() => {
        setSelectedCohortPlan(null);
    }, []);
    const cohortPlans = getCohortMasterPlans();
    // Resolve stubs using org lookup data
    const lookup = (0, useOrgLookupData_1.useOrgLookupData)(selectedOrgId || undefined);
    const resolvedPlans = (0, useResolvedDegreePlans_1.useResolvedDegreePlans)(cohortPlans, lookup.termsMap, lookup.coursesMap, lookup.programsMap, lookup.cohortsMap);
    // Find resolved version of selected plan
    const resolvedSelectedPlan = selectedCohortPlan
        ? resolvedPlans.find(p => p.id === selectedCohortPlan.id) || selectedCohortPlan
        : null;
    // Check if both degree plans and lookup data are loaded
    const isLoading = loading || lookup.loading;
    // Drill-down detail view
    if (resolvedSelectedPlan) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "page degree-plans-list-page", children: (0, jsx_runtime_1.jsx)(CohortPlanDetailView_1.CohortPlanDetailView, { plan: resolvedSelectedPlan, organizationName: selectedOrg === null || selectedOrg === void 0 ? void 0 : selectedOrg.name, onBack: handleBackToList }) }));
    }
    // Main list view
    return ((0, jsx_runtime_1.jsxs)("div", { className: "page degree-plans-list-page", children: [(0, jsx_runtime_1.jsx)("h1", { children: "Cohort List" }), (0, jsx_runtime_1.jsx)("p", { children: "View and manage cohort degree plans for your organization." }), (0, jsx_runtime_1.jsx)("div", { className: "page-controls", children: (0, jsx_runtime_1.jsx)(OrganizationSelect_1.OrganizationSelect, { value: selectedOrgId, onChange: handleOrganizationChange, required: true }) }), error && ((0, jsx_runtime_1.jsx)("div", { className: "page-error", role: "alert", children: (0, jsx_runtime_1.jsxs)("p", { children: ["Error: ", error] }) })), isLoading && (0, jsx_runtime_1.jsx)(LoadingSpinner_1.LoadingSpinner, { overlay: true, label: "Loading cohort plans..." }), selectedOrg && !isLoading && ((0, jsx_runtime_1.jsx)(CohortPlansDataTable_1.CohortPlansDataTable, { plans: resolvedPlans, onViewPlans: handleViewPlans, onMassUpdate: handleMassUpdate })), !selectedOrgId && !isLoading && ((0, jsx_runtime_1.jsx)("div", { className: "page-prompt", children: (0, jsx_runtime_1.jsx)("p", { children: "Please select an organization to view cohort plans." }) }))] }));
};
exports.default = DegreePlansListPage;
